import { type LucideIcon, MoreHorizontal } from 'lucide-react';
import { forwardRef, Fragment, type ReactNode } from 'react';

import { cn } from '~shared/shadcn/lib/utils';
import { Button, type ButtonProps } from '~shared/shadcn/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~shared/shadcn/ui/dropdown-menu';

import type { EnrollioTableActionTone } from './enrollio-table.types';

export interface EnrollioRowAction {
  id: string;
  label: string;
  icon?: LucideIcon;
  onSelect?: () => void;
  href?: string;
  disabled?: boolean;
  tone?: EnrollioTableActionTone;
  separatorBefore?: boolean;
}

const toneClass = (tone: EnrollioTableActionTone | undefined) =>
  tone === 'destructive'
    ? 'text-destructive focus:text-destructive'
    : tone === 'muted'
      ? 'text-muted-foreground'
      : undefined;

export const EnrollioRowActionButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, 'children'> & { label?: string }
>(function EnrollioRowActionButton(
  { label = 'Open row actions', ...props },
  ref,
) {
  return (
    <Button
      ref={ref}
      type='button'
      variant='ghost'
      size='icon'
      aria-label={label}
      {...props}
    >
      <MoreHorizontal className='size-4' />
    </Button>
  );
});

export function EnrollioRowActionMenu({
  actions,
  label,
  align = 'end',
}: {
  actions: Array<EnrollioRowAction>;
  label?: string;
  align?: 'start' | 'center' | 'end';
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <EnrollioRowActionButton label={label} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align}>
        {actions.map((action) => {
          const Icon = action.icon;
          const content = (
            <>
              {Icon ? <Icon className='mr-2 size-4' /> : null}
              {action.label}
            </>
          );
          return (
            <Fragment key={action.id}>
              {action.separatorBefore ? <DropdownMenuSeparator /> : null}
              <DropdownMenuItem
                disabled={action.disabled}
                className={toneClass(action.tone)}
                onSelect={action.onSelect}
                asChild={Boolean(action.href)}
              >
                {action.href ? <a href={action.href}>{content}</a> : content}
              </DropdownMenuItem>
            </Fragment>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const actionGroupClasses = {
  compact: 'gap-1',
  attendance: 'gap-2',
  makeup: 'flex-wrap gap-2',
  standard: 'gap-2',
} as const;

export function EnrollioRowActionGroup({
  children,
  variant = 'standard',
  className,
}: {
  children: ReactNode;
  variant?: keyof typeof actionGroupClasses;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center',
        actionGroupClasses[variant],
        className,
      )}
    >
      {children}
    </div>
  );
}

export function EnrollioRowActionGroupButton({
  icon: Icon,
  label,
  tone = 'default',
  iconOnly = false,
  className,
  ...props
}: React.ComponentProps<typeof Button> & {
  icon?: LucideIcon;
  label: string;
  tone?: EnrollioTableActionTone;
  iconOnly?: boolean;
}) {
  const variant =
    tone === 'destructive'
      ? 'destructive'
      : tone === 'primary'
        ? 'default'
        : tone === 'muted'
          ? 'ghost'
          : 'outline';
  return (
    <Button
      type='button'
      variant={variant}
      size={iconOnly ? 'icon' : 'sm'}
      aria-label={iconOnly ? label : undefined}
      className={className}
      {...props}
    >
      {Icon ? <Icon className='size-4' /> : null}
      {iconOnly ? <span className='sr-only'>{label}</span> : label}
    </Button>
  );
}
